import { useSuspenseQuery } from '@tanstack/react-query';
import { FlatfileRecord } from '@flatfile/hooks';
import { fetchAuctionCategories } from 'api/campaigns';
import { CHECKBOX_DESCRIPTION, FIELD_TYPE, submitAction } from 'components/imports/blueprintUtils';
import {
    MAX_STR_LENGTH,
    base,
    boolean,
    isFalse,
    isTrue,
    nullable,
    required,
} from 'components/imports/schemaUtils';
import { ImportStrategy, ImportStrategyProps } from 'components/imports/types';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { yup } from 'helpers/yup';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { ValidationError } from 'yup';

const auctionItemsSchema = yup.object().shape({
    name: required.label('Item name').max(MAX_STR_LENGTH),
    description: nullable.label('Description'),
    category: nullable.label('Category').max(MAX_STR_LENGTH),
    selling_format: required.label('Selling format').lowercase().oneOf(['auction', 'fixed']),
    total_quantity: base.label('Total quantity').when('selling_format', {
        is: (value: string | null) => value === 'fixed',
        then: schema => schema.required().asNumber(schema => schema.min(1)),
        otherwise: schema => schema.nullable().asNumber(schema => schema.nullable().min(1)),
    }),
    start_bid: base.label('Starting price').when('selling_format', {
        is: (value: string | null) => value === 'auction',
        then: schema => schema.required().asNumber(schema => schema.min(0)),
        otherwise: schema => schema.nullable().asNumber(schema => schema.nullable()),
    }),
    price: base.label('Buy now price').when('selling_format', {
        is: (value: string | null) => value === 'fixed',
        then: schema => schema.required().asNumber(schema => schema.min(1)),
        otherwise: schema => schema.nullable().asNumber(schema => schema.nullable().min(1)),
    }),
    reserve_price: nullable.label('Reserve price').asNumber(schema => schema.nullable().min(1)),
    retail_price: nullable.label('Item value').asNumber(schema => schema.nullable().min(1)),
    bid_increment: base.label('Bid increment').when('selling_format', {
        is: (value: string | null) => value === 'auction',
        then: schema => schema.required().asNumber(schema => schema.min(1)),
        otherwise: schema => schema.nullable().asNumber(schema => schema.nullable().min(1)),
    }),
    visibility: nullable.label('Visibility').trim().lowercase().oneOf(['public', 'private']),
    is_published: boolean.label('Publish').nullable(),
    requires_shipping: boolean.label('Requires shipping').nullable(),
    shipping_price: base.label('Shipping costs').when('requires_shipping', {
        is: isTrue,
        then: schema => schema.required().asNumber(schema => schema.min(0)),
        otherwise: schema => schema.nullable().asNumber(schema => schema.nullable().min(0)),
    }),
    purchase_message: nullable.label('Fulfillment details'),
});

const useAuctionItemImportStrategy = ({
    campaignId: consumerCampaignId = undefined,
}: ImportStrategyProps<'auction_items'>): ImportStrategy => {
    const { data: campaign } = useCurrentCampaign({ campaignId: consumerCampaignId });

    const { id: campaignId, auction_id: auctionId } = campaign;

    const { data: categoriesResults } = useSuspenseQuery({
        queryKey: campaignQueryKeys.auctionCategories(campaignId, auctionId!),
        queryFn: () => fetchAuctionCategories(campaignId, auctionId!),
    });

    const categories = categoriesResults?.data ?? [];

    return {
        type: 'auction_items',
        displayName: 'auction items',
        hasDownloadTemplate: false,
        context: {
            object_id: campaignId,
            object_type: 'campaign',
        },
        blueprint: {
            name: 'Import auction items',
            sheets: [
                {
                    name: 'Auction items',
                    description: 'Migrate auction items from another platform.',
                    slug: 'auction_items',
                    readonly: false,
                    allowAdditionalFields: true,
                    fields: [
                        {
                            label: 'Item name',
                            key: 'name',
                            type: FIELD_TYPE.string,
                            description:
                                'Use a name that clearly indicates to donors what auction item is being listed.',
                        },
                        {
                            label: 'Description',
                            key: 'description',
                            type: FIELD_TYPE.string,
                            description:
                                'Describe the auction item using clear and accurate details.',
                        },
                        {
                            label: 'Category',
                            key: 'category_id',
                            type: FIELD_TYPE.enum,
                            config: {
                                options: categories.map(cat => ({
                                    value: cat.id,
                                    label: `${cat.icon ?? ''}${cat.icon ? ' ' : ''}${cat.name}`,
                                })),
                            },
                        },
                        {
                            label: 'Selling format',
                            key: 'selling_format',
                            type: FIELD_TYPE.enum,
                            config: {
                                options: [
                                    { value: 'auction', label: 'Auction Style' },
                                    { value: 'fixed', label: 'Fixed price' },
                                ],
                            },
                        },
                        {
                            label: 'Starting price',
                            key: 'start_bid',
                            type: FIELD_TYPE.number,
                            description: 'Sets the lowest amount at which a bid can be placed.',
                        },
                        {
                            label: 'Bid increment',
                            key: 'bid_increment',
                            type: FIELD_TYPE.number,
                            description:
                                'Sets the minimum amount an auction price must be raised during bidding.',
                        },
                        {
                            label: 'Reserve price',
                            key: 'reserve_price',
                            type: FIELD_TYPE.number,
                            description:
                                'Sets the lowest acceptable winning price for this item. Items that do not reach their set reserve price have no winning bidder.',
                        },
                        {
                            label: 'Item value',
                            key: 'retail_price',
                            type: FIELD_TYPE.number,
                            description: 'Indicates the retail price of the item being listed.',
                        },
                        {
                            label: 'Buy now price',
                            key: 'price',
                            type: FIELD_TYPE.number,
                            description:
                                'Sets a fixed-price that bidders can purchase the item outright.',
                        },
                        {
                            label: 'Total quantity',
                            key: 'total_quantity',
                            type: FIELD_TYPE.number,
                            description:
                                'Sets the total quantity when selling multiple identical items.',
                        },
                        {
                            label: 'Visibility',
                            key: 'visibility',
                            type: FIELD_TYPE.enum,
                            config: {
                                options: [
                                    { value: 'public', label: 'Public' },
                                    { value: 'private', label: 'Private' },
                                ],
                            },
                        },
                        {
                            label: 'Publish',
                            key: 'is_published',
                            type: FIELD_TYPE.string,
                            description: `
                            Items are published by default.

                                To toggle on: \`true\`, \`1\`, \`yes\`, \`on\`
                            To toggle off: \`false\`, \`0\`, \`no\`, \`off\`
                            `,
                        },
                        {
                            label: 'Requires shipping',
                            key: 'requires_shipping',
                            type: FIELD_TYPE.string,
                            description: CHECKBOX_DESCRIPTION,
                        },
                        {
                            // only when requires shipping is on
                            label: 'Shipping costs',
                            key: 'shipping_price',
                            type: FIELD_TYPE.number,
                            description:
                                'Adds shipping and handling costs to the final price of an auction item when the item is being fulfilled by mail.',
                        },
                        {
                            label: 'Fulfillment details',
                            key: 'purchase_message',
                            type: FIELD_TYPE.string,
                        },
                    ],
                },
            ],
            actions: [submitAction],
        },
        recordHook: (record: FlatfileRecord) => {
            const data = record.value;

            const schema = auctionItemsSchema;

            try {
                schema.validateSync(data, { abortEarly: false });
            } catch (err) {
                if (!(err instanceof ValidationError)) {
                    return record;
                }

                if (!err.inner) {
                    record.addError(err.path!, err.message);

                    return record;
                }

                err.inner.forEach((e: any) => {
                    record.addError(e.path, e.message);
                });
            }

            if (
                data.selling_format === 'auction' &&
                data.price &&
                data.start_bid &&
                Number(data.price) < Number(data.start_bid)
            ) {
                record.addError('price', 'Buy now price must be higher than Starting price');
            }

            if (
                data.selling_format === 'auction' &&
                data.reserve_price &&
                data.start_bid &&
                Number(data.reserve_price) < Number(data.start_bid)
            ) {
                record.addError(
                    'reserve_price',
                    'Reserve price must be higher than Starting price',
                );
            }

            if (data.selling_format === 'fixed') {
                ['start_bid', 'bid_increment', 'reserve_price']
                    .filter(field => data[field] !== null)
                    .forEach(field =>
                        record.addWarning(
                            field,
                            'This field is unused when the selling type is "Fixed price"',
                        ),
                    );
            }

            if (data.selling_format === 'auction') {
                ['total_quantity']
                    .filter(field => data[field] !== null)
                    .forEach(field =>
                        record.addWarning(
                            field,
                            'This field is unused when the selling type is "Fixed price"',
                        ),
                    );
            }

            if (
                isFalse(data.requires_shipping as string, { explicit: false }) &&
                data.shipping_costs !== null
            ) {
                record.addWarning(
                    'shipping_costs',
                    'This field is unused when "Requires shipping" is off',
                );
            }

            return record;
        },
    };
};

export { useAuctionItemImportStrategy };
